import { BoxV2 as Box, Button, Flex } from 'portal-commons';
import { FunctionComponent, useEffect, useState } from 'react';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { brandController, campaignController } from '@/apis/core';
import { Loader } from '@/shared_elements';
import PDFViewer from '@/shared_elements/containers/PDFViewer';

interface LoaPreviewModalProps {
  readOnly?: boolean;
  brandUid?: string;
  campaignUid?: string;
  docUid: string;
  onCancel?: () => void;
  onSubmit?: () => void;
}

const LoaPreviewModal: FunctionComponent<LoaPreviewModalProps> = ({
  readOnly = false,
  brandUid,
  campaignUid,
  docUid,
  onCancel,
  onSubmit,
}) => {
  const [proceeding, setProceeding] = useState(false);
  const [url, setUrl] = useState('');

  const handleCancel = async () => {
    if (readOnly || campaignUid) {
      onCancel?.();
      return;
    }

    if (brandUid) {
      setProceeding(true);
      await brandController.cancelLoa(brandUid, docUid, {
        silent: true,
      });
      setProceeding(false);
    }
    onCancel?.();
  };

  const handleSend = async () => {
    if (brandUid) {
      setProceeding(true);
      const response = await brandController.sendLoa(brandUid, docUid);
      setProceeding(false);
      if (response) {
        onSubmit?.();
      }
    }
  };

  useEffect(() => {
    let active = true;
    let createdUrl = '';

    (async () => {
      setProceeding(true);
      try {
        let response;
        if (campaignUid) {
          response = await campaignController.downloadLoa(campaignUid, docUid);
        } else if (brandUid) {
          response = await brandController.downloadLoa(brandUid, docUid);
        }

        if (response && active) {
          const file = new File(
            [response.data],
            response.filename || 'LOA.pdf',
            {
              type: 'application/pdf',
            }
          );
          createdUrl = URL.createObjectURL(file);
          setUrl(createdUrl);
        }
      } catch (e) {
        console.error('Failed to download LOA for preview', e);
      } finally {
        if (active) {
          setProceeding(false);
        }
      }
    })();

    return () => {
      active = false;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [brandUid, campaignUid, docUid]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        background: 'rgba(73, 83, 93, 0.85)',
        backdropFilter: 'blur(7.5px)',
        zIndex: 1500,
      }}
      data-testid="loaPreviewModal"
    >
      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
          top: '96px',
          right: '114px',
          width: '48px',
          height: '48px',
          color: 't.white',
          fontSize: '28px',
          cursor: 'pointer',
        }}
        onClick={handleCancel}
        data-testid="close"
      >
        <FontAwesomeIcon icon={faXmark} />
      </Flex>

      {url ? (
        <PDFViewer
          sx={{
            position: 'absolute',
            top: '47px',
            right: 0,
            bottom: 0,
            left: 0,
            width: '842px',
            height: 'calc(100vh - 47px)',
            margin: '0 auto',
          }}
          width={842}
          url={url}
        />
      ) : (
        <Loader hocFlag />
      )}

      {!readOnly && !campaignUid && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            bottom: '40px',
            left: 0,
            width: 'fit-content',
            padding: '24px 32px',
            margin: 'auto',
            borderRadius: '8px',
            background: 't.white',
            boxShadow: '0 0 16px 0 rgba(0, 0, 0, 0.20)',
          }}
        >
          <Flex sx={{ gap: 'xl', padding: '0 55.5px' }}>
            <Button
              shape="square"
              variant="outline"
              disabled={proceeding}
              onClick={handleCancel}
              data-testid="cancel"
            >
              Cancel
            </Button>
            <Button
              shape="square"
              disabled={proceeding}
              onClick={handleSend}
              data-testid="submit"
            >
              Send Letter
            </Button>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default LoaPreviewModal;
